import { inject, Injectable, signal } from '@angular/core';
import {
  Consensus,
  ConsensusRow,
} from '@api-response-types/consensus.response';
import {
  rowMatchesScope,
  type ReconciliationScope,
} from '@api-types/sse/reconciliation-scope';
import type { FilterValue } from '@api-types/types/filter-value.type';
import { Store } from '@ngrx/store';
import { ConsensusHttpClient } from 'demand-forecasting/services/http/consensus-http-client';
import { firstValueFrom } from 'rxjs';
import { selectConsensusAppliedFiltersAsRecord } from '../../filter/filter.selectors';
import {
  selectConsensusFilterCombinationIdsArray,
  selectPlanningHierarchy,
  selectSelectedConsensusTemplateId,
} from '../../router-state/router.selectors';

/**
 * Composite cache key — `${consensusTemplateId}_${dateValue}`. The same row
 * path carries distinct value sets across months and across template views.
 */
const cacheKey = (consensusTemplateId: string, dateValue: string): string =>
  `${consensusTemplateId}_${dateValue}`;

/**
 * Lazy per-month consensus-value cache. The structural counterpart of
 * {@link ConsensusPredictionsStateService}: `dimension-groups` returns only the
 * row skeleton, and this service loads the cell values for the visible months
 * async to the row, splitting leaf vs aggregated lookups exactly like the
 * predictions cache does.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsensusValuesStateService {
  private readonly store = inject(Store);
  private readonly consensusHttpClient = inject(ConsensusHttpClient);

  private readonly consensusTemplateId = this.store.selectSignal(
    selectSelectedConsensusTemplateId,
  );
  private readonly planningHierarchy = this.store.selectSignal(
    selectPlanningHierarchy,
  );
  private readonly consensusAppliedFilters = this.store.selectSignal(
    selectConsensusAppliedFiltersAsRecord,
  );
  private readonly filterCombinationIds = this.store.selectSignal(
    selectConsensusFilterCombinationIdsArray,
  );

  // Map<uniqueCombinationPath, Map<`${templateId}_${dateValue}`, Consensus[]>>
  private readonly valuesCache = signal<Map<string, Map<string, Consensus[]>>>(
    new Map(),
  );

  public valuesLoading = signal(false);

  // Bumped on every cache invalidation (eviction or clear). A value load
  // captures it before its request; the merge/backfill below then ignore any
  // month invalidated while that request was in flight, so a fetch that read
  // the pre-edit backend state can't re-add a stale month after its eviction.
  // The leaf path self-heals (no incremental guard, always refetches); the
  // aggregated guard skips an apparently-cached month, so without this a
  // resurrected stale value would never reload until a full page refresh.
  private cacheGeneration = 0;
  private readonly evictedAtGeneration = new Map<string, number>();

  // `${path}|${templateId}_${dateValue}` -> the request that will resolve it.
  // The grid refreshes its blocks several times during load and those refreshes
  // overlap, so a cache-only check (which runs before the first response lands)
  // issues the same rollup more than once.
  private readonly inFlightMonths = new Map<string, Promise<void>>();

  // The cache is keyed by path, but scope matching needs the dimensions the
  // path was built from. Recording them on load keeps the path serialisation
  // an internal detail of the backend instead of a format both apps must agree
  // on. A path with no recorded dimensions is evicted unconditionally — an
  // over-broad eviction only costs a reload, a missed one shows a stale value.
  private readonly dimensionsByPath = new Map<
    string,
    Record<string, FilterValue>
  >();

  // `${path}|${templateId}_${dateValue}` due for a refetch while its previous
  // value stays in the cache. The cell renders a spinner over it either way,
  // but the cell-state colour is read from the cached value — deleting the
  // entry stripped the colour, and with one SSE event per month that stripping
  // marched across the row. A failed reload drops the entry (see
  // `fetchAggregatedConsensusValues`), so a stale number is never uncovered.
  private readonly staleKeys = new Set<string>();

  private flightKey(
    uniqueCombinationPath: string,
    consensusTemplateId: string,
    dateValue: string,
  ): string {
    return `${uniqueCombinationPath}|${cacheKey(consensusTemplateId, dateValue)}`;
  }

  public clearCache(): void {
    this.cacheGeneration += 1;
    this.evictedAtGeneration.clear();
    // a request that started before the purge no longer merges (the generation
    // guard drops it), so its keys must not keep a later fetch waiting
    this.inFlightMonths.clear();
    this.dimensionsByPath.clear();
    this.staleKeys.clear();
    this.valuesCache.set(new Map());
  }

  /**
   * Drops the cached entries for the given months so the next fetch reloads
   * them. Called after a reconciliation completes: an edit cascades down into
   * every descendant and up into every ancestor SUM for that month.
   *
   * With `scopes`, only the rows that actually cascade are evicted.
   * `rowMatchesScope` accepts a row that omits a scope dimension (an ancestor)
   * or carries the same value (the edited node and its descendants), and
   * rejects siblings — so an edit on one row no longer drops the whole column
   * into a loading state. Without `scopes` every path is evicted, which is
   * what a grand-total edit needs anyway.
   */
  public evictMonths(
    dateValues: string[],
    scopes?: readonly ReconciliationScope[],
  ): void {
    const templateId = this.consensusTemplateId();
    if (!templateId || dateValues.length === 0) return;

    const affects = (path: string): boolean => {
      if (!scopes || scopes.length === 0) return true;
      const dimensions = this.dimensionsByPath.get(path);
      if (!dimensions) return true;
      return scopes.some((scope) => rowMatchesScope({ dimensions }, scope));
    };

    this.cacheGeneration += 1;
    const evictedPaths = new Set<string>();
    for (const path of this.valuesCache().keys()) {
      if (!affects(path)) continue;
      evictedPaths.add(path);
      for (const dateValue of dateValues) {
        this.staleKeys.add(this.flightKey(path, templateId, dateValue));
      }
    }
    for (const dateValue of dateValues) {
      this.evictedAtGeneration.set(
        cacheKey(templateId, dateValue),
        this.cacheGeneration,
      );
    }
    for (const path of evictedPaths) {
      for (const dateValue of dateValues) {
        this.inFlightMonths.delete(this.flightKey(path, templateId, dateValue));
      }
    }
  }

  /**
   * Loads consensus values for `rows` across `dateValues`, splitting into leaf
   * vs aggregated lookups. Mirrors
   * {@link ConsensusPredictionsStateService.loadPredictionsForRows}.
   */
  public async loadConsensusValuesForRows(
    rows: ConsensusRow[],
    currentLevel: number,
    dateValues: string[],
    configurationId: string,
  ): Promise<void> {
    if (rows.length === 0 || dateValues.length === 0) return;

    const startGeneration = this.cacheGeneration;
    const planningHierarchy = this.planningHierarchy();

    for (const row of rows) {
      this.dimensionsByPath.set(row.uniqueCombinationPath, row.dimensions);
    }

    const aggregationRows = rows.filter((r) => !r.isLeaf);
    const leafIds = rows
      .filter((r) => r.isLeaf)
      .map((r) => r.id)
      .filter((id): id is string => id !== null);

    const promises: Promise<void>[] = [];
    if (aggregationRows.length > 0 && currentLevel < planningHierarchy.length) {
      // one request for the whole month set: the backend rollup projects the
      // grouped dimension keys before the join, so a multi-month horizon no
      // longer spills its GROUP BY to disk. `loadAggregatedConsensusValues`
      // still narrows to the months that are actually missing from the cache.
      promises.push(
        this.loadAggregatedConsensusValues(
          aggregationRows,
          dateValues,
          configurationId,
        ),
      );
    }
    if (leafIds.length > 0) {
      promises.push(
        this.loadLeafConsensusValues(leafIds, dateValues, configurationId),
      );
    }

    await Promise.all(promises);
    this.backfillMissing(rows, dateValues, startGeneration);
  }

  public async loadLeafConsensusValues(
    dimensionGroupIds: string[],
    dateValues: string[],
    configurationId: string,
  ): Promise<void> {
    const consensusTemplateId = this.consensusTemplateId();
    if (
      !consensusTemplateId ||
      dateValues.length === 0 ||
      dimensionGroupIds.length === 0
    ) {
      return;
    }

    this.valuesLoading.set(true);
    const startGeneration = this.cacheGeneration;
    try {
      const response = await firstValueFrom(
        this.consensusHttpClient.getLeafConsensusValues({
          configurationId,
          consensusTemplateId,
          dateValues,
          dimensionGroupIds,
        }),
      );
      this.mergeIntoCache(
        consensusTemplateId,
        response.values,
        dateValues,
        startGeneration,
      );
    } finally {
      this.valuesLoading.set(false);
    }
  }

  public async loadAggregatedConsensusValues(
    rows: ConsensusRow[],
    dateValues: string[],
    configurationId: string,
  ): Promise<void> {
    const consensusTemplateId = this.consensusTemplateId();
    const planningHierarchy = this.planningHierarchy();

    if (
      !consensusTemplateId ||
      dateValues.length === 0 ||
      planningHierarchy.length === 0 ||
      rows.length === 0
    ) {
      return;
    }

    // request only (row, month) pairs that are neither cached nor already on
    // the wire — same incremental semantics as the predictions cache (adding
    // one month fetches one month)
    const cache = this.valuesCache();
    const needsFetch = (row: ConsensusRow, dateValue: string): boolean => {
      const key = this.flightKey(
        row.uniqueCombinationPath,
        consensusTemplateId,
        dateValue,
      );
      if (this.inFlightMonths.has(key)) return false;
      if (this.staleKeys.has(key)) return true;
      return !cache
        .get(row.uniqueCombinationPath)
        ?.has(cacheKey(consensusTemplateId, dateValue));
    };

    const uncachedRows = rows.filter((row) =>
      dateValues.some((d) => needsFetch(row, d)),
    );
    const missingDateValues = dateValues.filter((d) =>
      uncachedRows.some((row) => needsFetch(row, d)),
    );

    // captured before this call registers its own keys, so it never waits on
    // itself
    const pending = this.pendingRequestsFor(
      rows,
      dateValues,
      consensusTemplateId,
    );

    if (uncachedRows.length === 0 || missingDateValues.length === 0) {
      await Promise.all(pending);
      return;
    }

    const request = this.fetchAggregatedConsensusValues(
      uncachedRows,
      missingDateValues,
      configurationId,
      consensusTemplateId,
      planningHierarchy,
    );
    this.trackInFlight(
      uncachedRows,
      missingDateValues,
      consensusTemplateId,
      request,
    );

    await Promise.all([request, ...pending]);
  }

  private async fetchAggregatedConsensusValues(
    rows: ConsensusRow[],
    dateValues: string[],
    configurationId: string,
    consensusTemplateId: string,
    planningHierarchy: string[],
  ): Promise<void> {
    this.valuesLoading.set(true);
    const startGeneration = this.cacheGeneration;
    try {
      const response = await firstValueFrom(
        this.consensusHttpClient.getAggregatedConsensusValues({
          configurationId,
          consensusTemplateId,
          dateValues,
          planningHierarchy,
          // the rows' own dimensions become the index-friendly @> parent scope
          // backend-side; only the applied filters go through ->>
          dimensions: rows.map((row) => row.dimensions),
          filterCombinationIds: this.filterCombinationIds(),
          rawFilters: this.consensusAppliedFilters(),
        }),
      );
      this.mergeIntoCache(
        consensusTemplateId,
        response.values,
        dateValues,
        startGeneration,
      );
    } catch (error) {
      // the reload failed, so the retained value is the pre-edit one. Drop it
      // rather than let the spinner clear and present it as current.
      this.dropStaleEntries(rows, dateValues, consensusTemplateId);
      throw error;
    } finally {
      this.valuesLoading.set(false);
    }
  }

  /** Removes the values a failed reload was meant to replace. */
  private dropStaleEntries(
    rows: ConsensusRow[],
    dateValues: string[],
    consensusTemplateId: string,
  ): void {
    const next = new Map(this.valuesCache());
    let changed = false;
    for (const row of rows) {
      const inner = next.get(row.uniqueCombinationPath);
      for (const dateValue of dateValues) {
        const key = this.flightKey(
          row.uniqueCombinationPath,
          consensusTemplateId,
          dateValue,
        );
        if (!this.staleKeys.has(key)) continue;
        if (inner?.delete(cacheKey(consensusTemplateId, dateValue))) {
          changed = true;
        }
        this.staleKeys.delete(key);
      }
    }
    if (changed) this.valuesCache.set(next);
  }

  private pendingRequestsFor(
    rows: ConsensusRow[],
    dateValues: string[],
    consensusTemplateId: string,
  ): Promise<void>[] {
    const pending = new Set<Promise<void>>();
    for (const row of rows) {
      for (const dateValue of dateValues) {
        const request = this.inFlightMonths.get(
          this.flightKey(
            row.uniqueCombinationPath,
            consensusTemplateId,
            dateValue,
          ),
        );
        if (request) pending.add(request);
      }
    }
    return [...pending];
  }

  private trackInFlight(
    rows: ConsensusRow[],
    dateValues: string[],
    consensusTemplateId: string,
    request: Promise<void>,
  ): void {
    const keys = rows.flatMap((row) =>
      dateValues.map((d) =>
        this.flightKey(row.uniqueCombinationPath, consensusTemplateId, d),
      ),
    );
    // waiters must not inherit the initiator's rejection — a failed rollup is
    // already rendered as "loaded, no value" by the caller's backfill
    const settled = request.then(
      () => undefined,
      () => undefined,
    );
    for (const key of keys) this.inFlightMonths.set(key, settled);
    void settled.then(() => {
      for (const key of keys) {
        if (this.inFlightMonths.get(key) === settled) {
          this.inFlightMonths.delete(key);
        }
      }
    });
  }

  /**
   * Returns:
   *   - `undefined` if the month is not yet loaded (cell shows a loading state)
   *   - `null` if loaded but no value-bearing cell exists for the column
   *   - the {@link Consensus} otherwise
   */
  public getCachedConsensus(
    uniqueCombinationPath: string,
    month: string,
    consensusColumnId: string,
  ): Consensus | null | undefined {
    const templateId = this.consensusTemplateId();
    if (!templateId) return undefined;
    const monthValues = this.valuesCache()
      .get(uniqueCombinationPath)
      ?.get(cacheKey(templateId, month));
    if (monthValues === undefined) return undefined;
    return (
      monthValues.find((c) => c.consensusColumnId === consensusColumnId) ?? null
    );
  }

  private mergeIntoCache(
    consensusTemplateId: string,
    values: { uniqueCombinationPath: string; consensuses: Consensus[] }[],
    dateValues: string[],
    startGeneration: number,
  ): void {
    const freshDateValues = dateValues.filter(
      (dateValue) =>
        (this.evictedAtGeneration.get(
          cacheKey(consensusTemplateId, dateValue),
        ) ?? -1) <= startGeneration,
    );
    if (freshDateValues.length === 0) return;

    const newCache = new Map(this.valuesCache());

    for (const { uniqueCombinationPath, consensuses } of values) {
      let inner = newCache.get(uniqueCombinationPath);
      if (!inner) {
        inner = new Map();
        newCache.set(uniqueCombinationPath, inner);
      }
      for (const dateValue of freshDateValues) {
        inner.set(
          cacheKey(consensusTemplateId, dateValue),
          consensuses.filter((c) => c.dateValue === dateValue),
        );
        this.staleKeys.delete(
          this.flightKey(uniqueCombinationPath, consensusTemplateId, dateValue),
        );
      }
    }

    this.valuesCache.set(newCache);
  }

  /**
   * Marks every requested `(path, month)` as loaded with an empty cell list
   * when the response omitted it (no value-bearing cell), so the renderer can
   * tell "loaded, no value" (`[]`) from "not yet loaded" (`undefined`).
   */
  private backfillMissing(
    rows: ConsensusRow[],
    dateValues: string[],
    startGeneration: number,
  ): void {
    const consensusTemplateId = this.consensusTemplateId();
    if (!consensusTemplateId || rows.length === 0 || dateValues.length === 0) {
      return;
    }

    const newCache = new Map(this.valuesCache());
    let changed = false;

    for (const row of rows) {
      let inner = newCache.get(row.uniqueCombinationPath);
      if (!inner) {
        inner = new Map();
        newCache.set(row.uniqueCombinationPath, inner);
        changed = true;
      }
      for (const dateValue of dateValues) {
        const key = cacheKey(consensusTemplateId, dateValue);
        // evicted mid-load → its reload is still pending; leave it `undefined`
        // (loading) instead of marking it "loaded, no value" (`[]`).
        if ((this.evictedAtGeneration.get(key) ?? -1) > startGeneration) {
          continue;
        }
        if (!inner.has(key)) {
          inner.set(key, []);
          changed = true;
        }
      }
    }

    if (changed) this.valuesCache.set(newCache);
  }
}
